import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { scaledFormatYAxis, getMaxValueFromChartLevels } from 'utils/reports';
import getNextRoundUpValue from '../utils';
import 'components/Charts/StackedBarChart/styles.css';

const DEFAULT_MARGIN_TOP = 24;
const DEFAULT_MARGIN_BOTTOM = 72;
const DEFAULT_MARGIN_LEFT = 56;
const DEFAULT_MARGIN_RIGHT = 20;
const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 360;
const DEFAULT_BAR_WIDTH = 18;
const DEFAULT_LEGEND_GROUP = 3;

/**
 * StackedBarChart
 * Recharts implementation with backward-compatible props.
 */
export function StackedBarChart({
  data = [],
  chartMarginTop = DEFAULT_MARGIN_TOP,
  chartMarginRight = DEFAULT_MARGIN_RIGHT,
  chartMarginBottom = DEFAULT_MARGIN_BOTTOM,
  chartMarginLeft = DEFAULT_MARGIN_LEFT,
  width,
  height = DEFAULT_HEIGHT,
  yAxisTitle = '',
  barWidth = DEFAULT_BAR_WIDTH,
  xAxisLabelRotate = 0,
  xAxisLabelMarginLeft = 0,
  xAxisLabelMarginTop = 0,
  barColors = [],
  hideLegends = false,
  legendGroup = DEFAULT_LEGEND_GROUP,
  yAxisMaxValue = null,
  yAxisTickDecimals,
  id = 'StackedBarChart',
}) {
  const theme = useTheme();
  const maxValueData = getMaxValueFromChartLevels(data) || 0;
  const yMax = yAxisMaxValue || getNextRoundUpValue(maxValueData);
  const backgroundColor = 'var(--color-bg)';
  const tooltipTextColor = theme.palette.mode === 'dark' ? '#e2e8f0' : '#0f172a';
  const tooltipBgColor = theme.palette.mode === 'dark' ? '#111827' : '#ffffff';
  const tooltipBorder = theme.palette.mode === 'dark' ? '#334155' : '#d1d5db';
  const legendTextColor = theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569';
  const legendFormatter = (value) => <span style={{ color: legendTextColor }}>{value}</span>;

  const { chartData, barsConfig } = useMemo(() => {
    const rows = [];
    const bars = [];
    const addedBarKeys = new Set();

    data.forEach((item) => {
      const row = { name: item.group };
      (item.values || []).forEach((stackItems, stackIndex) => {
        (stackItems || []).forEach((subItem) => {
          const uniqueKey = `${stackIndex}__${subItem.name}`;
          row[uniqueKey] = Number(subItem.value) || 0;

          if (!addedBarKeys.has(uniqueKey)) {
            addedBarKeys.add(uniqueKey);
            const matchedColor = barColors.find((color) => color.name === subItem.name);
            bars.push({
              dataKey: uniqueKey,
              legendName: subItem.name,
              stackId: `stack-${stackIndex}`,
              fill: matchedColor ? matchedColor.color : subItem.color,
            });
          }
        });
      });
      rows.push(row);
    });

    return { chartData: rows, barsConfig: bars };
  }, [data, barColors]);

  const legendPayload = useMemo(() => {
    const uniqueByName = [];
    const seenNames = new Set();

    barsConfig.forEach((bar) => {
      if (!seenNames.has(bar.legendName)) {
        seenNames.add(bar.legendName);
        uniqueByName.push({
          id: bar.legendName,
          value: bar.legendName,
          type: 'line',
          color: bar.fill,
        });
      }
    });

    return uniqueByName;
  }, [barsConfig]);

  const legendProps =
    legendPayload.length > legendGroup
      ? { wrapperStyle: { paddingTop: 12, lineHeight: '20px' } }
      : { wrapperStyle: { paddingTop: 12 } };
  const accessibleYAxisTitle = yAxisTitle || 'Energy';

  return (
    <div
      id={id}
      style={{ backgroundColor, width: width || DEFAULT_WIDTH, maxWidth: '100%', height }}
      data-testid="stackedbar-chart-component"
      className="chart"
    >
      {chartData.length === 0 && (
        <span
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {accessibleYAxisTitle}
        </span>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart
          data={chartData}
          margin={{
            top: chartMarginTop,
            right: chartMarginRight,
            bottom: chartMarginBottom,
            left: chartMarginLeft,
          }}
          barCategoryGap="28%"
        >
          <CartesianGrid strokeDasharray="10 10" />
          <XAxis
            dataKey="name"
            angle={xAxisLabelRotate}
            textAnchor="middle"
            dx={xAxisLabelMarginLeft}
            dy={xAxisLabelMarginTop}
            tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
            interval="preserveStartEnd"
            tickMargin={10}
            axisLine={false}
            tickLine={false}
            label={undefined}
          />
          <YAxis
            domain={[0, yMax]}
            tickFormatter={(tick) =>
              scaledFormatYAxis(yAxisMaxValue, maxValueData, tick, yAxisTickDecimals)
            }
            tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
            tickMargin={10}
            axisLine={false}
            tickLine={false}
            label={
              yAxisTitle
                ? {
                    value: yAxisTitle,
                    angle: -90,
                    position: 'insideLeft',
                    fill: 'var(--color-text-muted)',
                    dx: -28,
                  }
                : undefined
            }
          />
          <Tooltip
            labelFormatter={(label) => `Date: ${label}`}
            contentStyle={{
              backgroundColor: tooltipBgColor,
              border: `1px solid ${tooltipBorder}`,
              borderRadius: 6,
            }}
            labelStyle={{ color: tooltipTextColor, fontWeight: 600 }}
            itemStyle={{ color: tooltipTextColor }}
          />
          {!hideLegends && (
            <Legend
              payload={legendPayload}
              verticalAlign="bottom"
              formatter={legendFormatter}
              {...legendProps}
            />
          )}
          {barsConfig.map((bar) => (
            <Bar
              key={bar.dataKey}
              dataKey={bar.dataKey}
              stackId={bar.stackId}
              fill={bar.fill}
              name={bar.legendName}
              barSize={barWidth}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}

StackedBarChart.propTypes = {
  data: PropTypes.array.isRequired,
  chartMarginTop: PropTypes.number,
  chartMarginRight: PropTypes.number,
  chartMarginBottom: PropTypes.number,
  chartMarginLeft: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.number,
  yAxisTitle: PropTypes.string,
  barWidth: PropTypes.number,
  id: PropTypes.string.isRequired,
  barColors: PropTypes.array.isRequired,
  xAxisLabelRotate: PropTypes.number,
  xAxisLabelMarginLeft: PropTypes.number,
  xAxisLabelMarginTop: PropTypes.number,
  hideLegends: PropTypes.bool,
  legendGroup: PropTypes.number,
  yAxisMaxValue: PropTypes.number,
  yAxisTickDecimals: PropTypes.number,
};

export default StackedBarChart;
